import { useContext, useEffect, useRef } from "react";
import { Optional } from "sonobello.utilities.react.mui";

import AppContext from "./AppContext";
import { DisqualifyingReasonsType } from "./dtos/DisqualifyingReason";
import { bodyMassIndex } from "./utils/Constants";
import { loadFromLocal, saveToLocal } from "./utils/LocalStorage";
import { useObx } from "./utils/UseObx";
import AllMedicalQuestionsEvaluator from "./V2/Steps/Medical/Compositions/AllMedicalQuestionsEvaluator";
import { evaluatePatientDisqualification } from "./V2/Types/Entities/Patient";

/**
 * A record of the medical characteristics of a patient which may qualify or disqualify them from receiving business
 * services.
 */
interface IPatientQualification {
  /** The height of the patient in inches. */
  heightInches?: number;
  /** The weight of the patient in imperial pounds. */
  weight?: number;
  /** The calculated body mass index of the patient from the height and weight parameters. */
  bodyMassIndex?: number;
  /** The date of birth of the patient as an ISO date. */
  dateOfBirth?: string;
  /** Flag indicating if the patient successfully passed all medical qualification questions. */
  isMedicallyQualified?: boolean;
}

class PatientQualificationRequest {
  /** The id of the contact with which the qualification is associated. */
  contactId: string;
  /** The id of the patient whom has been assessed for qualification. */
  patientId: string;
  /** The results of the qualification assessment. */
  payload: IPatientQualification;

  constructor(contactId: string, patientId: string, payload: IPatientQualification) {
    this.contactId = contactId;
    this.patientId = patientId;
    this.payload = payload;
  }

  /** Determines whether two instances of PatientQualificationRequest are equal. */
  static equals = (a: PatientQualificationRequest, b: PatientQualificationRequest): boolean => {
    return (
      a.contactId === b.contactId &&
      a.patientId === b.patientId &&
      a.payload.dateOfBirth === b.payload.dateOfBirth &&
      a.payload.heightInches === b.payload.heightInches &&
      a.payload.weight === b.payload.weight &&
      a.payload.isMedicallyQualified === b.payload.isMedicallyQualified
    );
  };
}

const localStorageKey = "qualificationRecord";

const useContactPatientQualification = () => {
  const { contactId, medicalForm, patient } = useContext(AppContext);

  const lastUpdatedQualificationRef = useRef(loadFromLocal<PatientQualificationRequest>(localStorageKey));

  const { res: postResponse, setReq: setPostRequest } = useObx<
    IPatientQualification,
    IPatientQualification,
    Pick<PatientQualificationRequest, "contactId" | "patientId">
  >();

  useEffect(() => {
    if (!patient?.id) return;

    let disqualifyingReason: Optional<DisqualifyingReasonsType> = undefined;

    if (patient.height && patient.weight && patient.dateOfBirth)
      disqualifyingReason = evaluatePatientDisqualification(patient as never);

    if (!disqualifyingReason && !AllMedicalQuestionsEvaluator.isComplete(medicalForm)) return;

    const qualificationPayload: IPatientQualification = {
      heightInches: undefined,
      weight: patient.weight || undefined,
      bodyMassIndex: undefined,
      dateOfBirth: patient.dateOfBirth || undefined,
      isMedicallyQualified:
        !disqualifyingReason && !AllMedicalQuestionsEvaluator.isNonCandidate(medicalForm) ? true : false
    };

    if (patient.height) {
      qualificationPayload.heightInches = patient.height.feet * 12 + patient.height.inches;
    }

    if (qualificationPayload.heightInches && patient.weight) {
      qualificationPayload.bodyMassIndex = bodyMassIndex(patient.weight, qualificationPayload.heightInches);
    }

    const qualificationRequest = new PatientQualificationRequest(contactId, patient.id, qualificationPayload);

    if (
      lastUpdatedQualificationRef.current &&
      PatientQualificationRequest.equals(lastUpdatedQualificationRef.current, qualificationRequest)
    )
      return;

    setPostRequest({
      url: `Legacy/V0/Contacts/${qualificationRequest.contactId}/Customers/${qualificationRequest.patientId}/Qualifications`,
      method: "post",
      payload: qualificationRequest.payload,
      custom: { contactId: qualificationRequest.contactId, patientId: qualificationRequest.patientId }
    });
  }, [patient, contactId, medicalForm]);

  useEffect(() => {
    if (!postResponse || !postResponse.request.custom) return;
    const newRequest = new PatientQualificationRequest(
      postResponse.request.custom.contactId,
      postResponse.request.custom.patientId,
      postResponse.payload
    );
    lastUpdatedQualificationRef.current = newRequest;
    saveToLocal(newRequest, localStorageKey);
  }, [postResponse]);
};

export default useContactPatientQualification;
